<script setup lang="ts">
import { computed, ref } from "vue";
import { useEventListener } from "@vueuse/core";
import type { Pull } from "../lib/types";
import { COOLDOWNS, CLASS_COLOR } from "../config/cooldowns";
import Duration from "./Duration.vue";
import Tooltip from "./Tooltip.vue";

const props = defineProps<{ pull: Pull; time: number }>();
const emit = defineEmits<{ seek: [ms: number] }>();

const dur = computed(() => props.pull.meta.duration_ms);

// Position as a percent across the track; 0 for a zero-length pull (no divide-by-zero).
const pct = (t: number) => (dur.value > 0 ? (t / dur.value) * 100 : 0);

// Only the casts that are tracked raid cooldowns.
const cds = computed(() =>
  props.pull.casts
    .filter((c) => COOLDOWNS[c.spell_id])
    .map((c) => ({ ...c, def: COOLDOWNS[c.spell_id], left: pct(c.t) })),
);

const deaths = computed(() =>
  props.pull.deaths.map((d) => ({ ...d, left: pct(d.t) })),
);

const playLeft = computed(() => pct(props.time));

const track = ref<HTMLElement | null>(null);
const scrubbing = ref(false);

function seekFromEvent(e: PointerEvent) {
  const el = track.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const f = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  emit("seek", f * dur.value);
}
function down(e: PointerEvent) {
  scrubbing.value = true;
  seekFromEvent(e);
}

// Listen on window so a drag that leaves the track keeps scrubbing, and
// releasing anywhere ends it — without pointer capture or its try/catch.
useEventListener(window, "pointermove", (e) => scrubbing.value && seekFromEvent(e));
useEventListener(window, "pointerup", () => (scrubbing.value = false));
</script>

<template>
  <div class="select-none">
    <!-- cooldown lane -->
    <div class="relative mb-1 h-6">
      <Tooltip
        v-for="(c, i) in cds"
        :key="i"
        class="absolute top-0 -translate-x-1/2"
        :style="{ left: c.left + '%' }"
      >
        <span
          class="block h-5 w-[3px] rounded-sm"
          :style="{ background: CLASS_COLOR[c.def.cls] }"
        ></span>
        <template #content>
          {{ c.def.name }} — {{ c.source }} @ <Duration :timestamp="c.t" />
        </template>
      </Tooltip>
    </div>

    <!-- scrubbable track with deaths + playhead -->
    <div
      ref="track"
      class="relative h-8 cursor-pointer rounded bg-zinc-900"
      @pointerdown="down"
    >
      <Tooltip
        v-for="(d, i) in deaths"
        :key="i"
        class="absolute top-1/2 -translate-x-1/2 -translate-y-1/2"
        :style="{ left: d.left + '%' }"
      >
        <span class="block h-2 w-2 rotate-45 bg-red-500"></span>
        <template #content> {{ d.name }} died @ <Duration :timestamp="d.t" /> </template>
      </Tooltip>
      <div
        class="pointer-events-none absolute top-0 bottom-0 w-0.5 -translate-x-1/2 bg-amber-400"
        :style="{ left: playLeft + '%' }"
      ></div>
    </div>
  </div>
</template>
